package com.nnod.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.graphics.Shape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonColors
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.OutlinedButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.nnod.ui.theme.NnodPurpleDeep
import com.nnod.ui.theme.NnodPurpleMid

/**
 * Nnod-branded filled button.
 *
 * Wraps Material 3 [Button] with Nnod primary colour.
 *
 * @param onClick Called when the button is clicked.
 * @param modifier Modifier to be applied to the button.
 * @param enabled Whether the button is enabled.
 * @param colors Button color overrides. Defaults to [ButtonDefaults.buttonColors].
 * @param contentPadding Padding values applied to the button's content.
 * @param content The composable content (typically a [Text]).
 */
@Composable
fun NnodButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    colors: ButtonColors = ButtonDefaults.buttonColors(),
    contentPadding: PaddingValues = ButtonDefaults.ContentPadding,
    content: @Composable RowScope.() -> Unit,
) {
    Button(
        onClick = onClick,
        modifier = modifier,
        enabled = enabled,
        colors = colors,
        contentPadding = contentPadding,
        content = content,
    )
}

/**
 * Nnod-branded outlined button.
 *
 * Wraps Material 3 [OutlinedButton].
 *
 * @param onClick Called when the button is clicked.
 * @param modifier Modifier to be applied to the button.
 * @param enabled Whether the button is enabled.
 * @param colors Button color overrides. Defaults to [ButtonDefaults.outlinedButtonColors].
 * @param contentPadding Padding values applied to the button's content.
 * @param content The composable content (typically a [Text]).
 */
@Composable
fun NnodOutlinedButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    shape: Shape = ButtonDefaults.outlinedShape,
    colors: ButtonColors = ButtonDefaults.outlinedButtonColors(),
    contentPadding: PaddingValues = ButtonDefaults.ContentPadding,
    content: @Composable RowScope.() -> Unit,
) {
    OutlinedButton(
        onClick = onClick,
        modifier = modifier,
        enabled = enabled,
        shape = shape,
        colors = colors,
        contentPadding = contentPadding,
        content = content,
    )
}

/**
 * Nnod-branded gradient button matching the App Prototype design.
 *
 * Uses a diagonal linear gradient from [NnodPurpleDeep] to [NnodPurpleMid],
 * rounded corners at 16 dp, and white content colour.
 *
 * @param onClick Called when the button is clicked.
 * @param modifier Modifier to be applied to the button.
 * @param enabled Whether the button is interactive.
 * @param gradientColors The gradient colors to apply. Defaults to [NnodPurpleDeep] and [NnodPurpleMid].
 * @param content The composable content (typically a [Text]).
 */
@Composable
fun NnodGradientButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    gradientColors: List<Color> = listOf(NnodPurpleDeep, NnodPurpleMid),
    content: @Composable RowScope.() -> Unit,
) {
    val shape = RoundedCornerShape(16.dp)
    val gradient = Brush.linearGradient(
        colors = if (enabled) gradientColors
                 else listOf(Color(0xFFD1C4E9), Color(0xFFD1C4E9)),
    )
    Box(
        modifier = modifier
            .clip(shape)
            .background(gradient)
            .clickable(enabled = enabled, onClick = onClick)
            .padding(vertical = 15.dp, horizontal = 20.dp),
        contentAlignment = Alignment.Center,
    ) {
        Row(
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
            content = content,
        )
    }
}
